import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Popover,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tag,
  Timeline,
  Tooltip,
  Typography,
} from "antd";
import {
  CalculatorOutlined,
  AuditOutlined,
  BankOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  DownloadOutlined,
  DollarCircleOutlined,
  EditOutlined,
  EyeOutlined,
  FormOutlined,
  FileExcelOutlined,
  LockOutlined,
  MailOutlined,
  PlusOutlined,
  ReloadOutlined,
  RollbackOutlined,
  SendOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { usePayrollRuns } from "../../../hooks/finance/usePayrollRuns";
import useValuesStore from "../../../store/values-store";
import Utilities from "../../../dependencies/custom/react-utilities";
import domainSettings from "../../../dependencies/custom/settings";
import {
  formatPayrollMoney,
  payrollAmountInputProps,
} from "../../../components/finance/payroll/payrollAmountInput";

const { Text, Title } = Typography;
const dateValue = (value) => (value ? dayjs(value) : null);
const isoDate = (value) => (value ? value.format("YYYY-MM-DD") : null);
const money = (value) =>
  `UGX ${Number(value || 0).toLocaleString("en-UG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
const workflowColors = {
  Draft: "default",
  Submitted: "blue",
  "In Review": "gold",
  Approved: "green",
  Finalized: "purple",
  "Partially Paid": "orange",
  Paid: "green",
  Reversed: "volcano",
  Rejected: "red",
  Cancelled: "default",
};

const warningMessages = (warnings) => {
  if (Array.isArray(warnings)) return warnings.filter(Boolean).map(String);
  return warnings ? [String(warnings)] : [];
};

const WarningList = ({ warnings }) => {
  const messages = warningMessages(warnings);
  if (!messages.length) return null;
  return (
    <ul style={{ margin: 0, paddingLeft: 20 }}>
      {messages.map((warning, index) => (
        <li key={`${warning}-${index}`}>{warning}</li>
      ))}
    </ul>
  );
};

const SaccoLoanInformation = ({ snapshot }) => {
  const loans = snapshot?.loans || [];
  if (!loans.length) return null;
  return (
    <Card title="SACCO Loan Information" size="small">
      <Table
        rowKey={(row, index) => row.loanReference || index}
        dataSource={loans}
        pagination={false}
        size="small"
        scroll={{ x: 850 }}
        columns={[
          { title: "Loan reference", dataIndex: "loanReference" },
          {
            title: "Outstanding balance",
            dataIndex: "outstandingBalance",
            align: "right",
            render: money,
          },
          {
            title: "Next instalment and due date",
            render: (_, row) => (
              <Space direction="vertical" size={0}>
                <Text>
                  {row.nextInstallmentAmount == null
                    ? "—"
                    : money(row.nextInstallmentAmount)}
                </Text>
                {row.nextInstallmentDueDate && (
                  <Text type="secondary">
                    Due{" "}
                    {dayjs(row.nextInstallmentDueDate).format("DD MMM YYYY")}
                  </Text>
                )}
              </Space>
            ),
          },
          {
            title: "Arrears / penalties",
            render: (_, row) => (
              <Space direction="vertical" size={0}>
                <Text>Arrears: {money(row.arrearsAmount)}</Text>
                <Text>Penalties: {money(row.penaltiesAmount)}</Text>
              </Space>
            ),
          },
          {
            title: "As at date",
            render: () =>
              snapshot.asAtDate
                ? dayjs(snapshot.asAtDate).format("DD MMM YYYY")
                : "—",
          },
        ]}
      />
    </Card>
  );
};

const PayrollRunsManagement = () => {
  const navigate = useNavigate();
  const {
    periods,
    inputs,
    profiles,
    components,
    paymentAccounts,
    loading,
    saving,
    fetchPeriods,
    fetchInputs,
    fetchInputOptions,
    saveInput,
    deleteInput,
    savePeriod,
    generateDraft,
    deletePeriod,
    deleteRun,
    transitionRun,
    fetchRun,
    downloadPayslip,
    downloadCombinedPayslips,
    downloadDraftPayslips,
    downloadPayslipsZip,
    downloadFinalSchedule,
    emailPayslip,
    emailAllPayslips,
    downloadPaymentExport,
    previewPaymentExport,
    recordPayment,
    markPayrollPaid,
    reversePayroll,
  } = usePayrollRuns();
  const valuesStore = useValuesStore();
  const [periodForm] = Form.useForm();
  const [inputForm] = Form.useForm();
  const [rejectionForm] = Form.useForm();
  const [paymentForm] = Form.useForm();
  const [bulkPaymentForm] = Form.useForm();
  const [reversalForm] = Form.useForm();
  const [periodOpen, setPeriodOpen] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [selectedRun, setSelectedRun] = useState(null);
  const [inputPeriod, setInputPeriod] = useState(null);
  const [inputOpen, setInputOpen] = useState(false);
  const [selectedInput, setSelectedInput] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [bulkPaymentOpen, setBulkPaymentOpen] = useState(false);
  const [reversalOpen, setReversalOpen] = useState(false);
  const [exportState, setExportState] = useState(null);
  const [exportPreview, setExportPreview] = useState(null);
  const [reviewDashboard, setReviewDashboard] = useState(null);
  const [reviewComments, setReviewComments] = useState([]);
  const [emailSummary, setEmailSummary] = useState(null);
  const [expandedEmployeeRowKeys, setExpandedEmployeeRowKeys] = useState([]);
  const periodStart = Form.useWatch("startDate", periodForm);
  const payrollType = Form.useWatch("payrollType", periodForm);

  useEffect(() => {
    fetchPeriods();
    fetchInputOptions();
  }, [fetchInputOptions, fetchPeriods]);

  const totals = useMemo(
    () =>
      periods.reduce(
        (summary, period) => {
          if (!period.run) return summary;
          summary.runs += 1;
          summary.employees += Number(period.run.employeeCount || 0);
          summary.netPay += Number(period.run.totalNetPay || 0);
          return summary;
        },
        { runs: 0, employees: 0, netPay: 0 },
      ),
    [periods],
  );

  const workflowPermissions = useMemo(
    () =>
      new Set(
        (valuesStore.permissions || []).map((permission) => permission.name),
      ),
    [valuesStore.permissions],
  );
  const canWorkflow = useCallback(
    (permissionName) => workflowPermissions.has(permissionName),
    [workflowPermissions],
  );

  const fetchEmailSummary = useCallback(
    async (runId) => {
      const url =
        domainSettings.urls.finance.payrollRunPayslipEmailStatus.replace(
          ":id",
          runId,
        );
      const response = await Utilities.fetchWithAuth(
        "GET",
        url,
        null,
        navigate,
      );
      if (response.ok) setEmailSummary(response.data);
    },
    [navigate],
  );

  useEffect(() => {
    if (
      selectedRun &&
      ["Finalized", "Partially Paid", "Paid"].includes(selectedRun.status)
    ) {
      fetchEmailSummary(selectedRun.runId);
    } else {
      setEmailSummary(null);
    }
  }, [fetchEmailSummary, selectedRun]);

  const reviewUrl = (template, id) => template.replace(":id", id);
  const loadReview = useCallback(
    async (runId) => {
      if (!runId || !canWorkflow("finance_payroll_review_view")) return;
      const [dashboard, comments] = await Promise.all([
        Utilities.fetchWithAuth(
          "GET",
          reviewUrl(
            domainSettings.urls.finance.payrollRunReviewDashboard,
            runId,
          ),
          null,
          navigate,
        ),
        Utilities.fetchWithAuth(
          "GET",
          `${domainSettings.urls.finance.payrollReviewComments}?run_id=${runId}`,
          null,
          navigate,
        ),
      ]);
      if (dashboard.ok) setReviewDashboard(dashboard.data);
      if (comments.ok)
        setReviewComments(Array.isArray(comments.data) ? comments.data : []);
    },
    [canWorkflow, navigate],
  );

  const selectedRunId = selectedRun?.runId;
  const selectedCalculationVersion = selectedRun?.calculationVersion;
  const activeApprovalStep = (selectedRun?.approvalSteps || []).find((step) =>
    ["Pending", "In Progress"].includes(step.status),
  );
  const completingReviewStep = activeApprovalStep?.stepType === "Review";
  const canCompleteApprovalStep = canWorkflow(
    completingReviewStep
      ? "finance_payroll_workflow_review"
      : "finance_payroll_workflow_approve",
  );
  useEffect(() => {
    if (selectedRunId) loadReview(selectedRunId);
    else {
      setReviewDashboard(null);
      setReviewComments([]);
    }
  }, [selectedRunId, selectedCalculationVersion, loadReview]);

  useEffect(() => {
    setExpandedEmployeeRowKeys([]);
  }, [selectedRunId]);

  const reasonAction = (title, action) => {
    let reason = "";
    Modal.confirm({
      title,
      content: (
        <Input.TextArea
          rows={3}
          placeholder="Reason (required)"
          onChange={(e) => {
            reason = e.target.value;
          }}
        />
      ),
      onOk: async () => {
        if (!reason.trim()) throw new Error("Enter a reason");
        const response = await Utilities.fetchWithAuth(
          "POST",
          action,
          { reason },
          navigate,
        );
        if (!response.ok) {
          Utilities.showError(response, "Action failed");
          throw new Error("Action failed");
        }
        await loadReview(selectedRun.runId);
      },
    });
  };

  const addReviewComment = (target = {}) => {
    let comment = "";
    Modal.confirm({
      title: target.parentId
        ? "Reply to review comment"
        : `Add ${target.targetType || "Run"} review comment`,
      content: (
        <Input.TextArea
          rows={4}
          placeholder="Explain what must be checked or corrected"
          onChange={(e) => {
            comment = e.target.value;
          }}
        />
      ),
      onOk: async () => {
        if (!comment.trim()) throw new Error("Enter a comment");
        const response = await Utilities.fetchWithAuth(
          "POST",
          domainSettings.urls.finance.payrollReviewComments,
          {
            runId: selectedRun.runId,
            targetType: target.targetType || "Run",
            profileId: target.profileId,
            componentCode: target.componentCode,
            componentName: target.componentName,
            parentId: target.parentId,
            comment,
            isBlocking: target.parentId ? false : true,
          },
          navigate,
        );
        if (!response.ok) {
          Utilities.showError(response, "Could not add comment");
          throw new Error("Failed");
        }
        await loadReview(selectedRun.runId);
      },
    });
  };

  const cancelPayroll = () => {
    let reason = "";
    Modal.confirm({
      title: "Cancel this payroll safely?",
      content: (
        <>
          <Alert
            type="warning"
            showIcon
            message="The payroll stays in audit history and cannot be edited after cancellation."
            style={{ marginBottom: 12 }}
          />
          <Input.TextArea
            rows={3}
            placeholder="Cancellation reason (required)"
            onChange={(e) => {
              reason = e.target.value;
            }}
          />
        </>
      ),
      okButtonProps: { danger: true },
      okText: "Cancel payroll",
      onOk: async () => {
        if (!reason.trim()) throw new Error("Enter a reason");
        const response = await Utilities.fetchWithAuth(
          "POST",
          reviewUrl(
            domainSettings.urls.finance.payrollRunCancel,
            selectedRun.runId,
          ),
          { reason },
          navigate,
        );
        if (!response.ok) {
          Utilities.showError(response, "Could not cancel payroll");
          throw new Error("Failed");
        }
        setSelectedRun(response.data);
        fetchPeriods();
      },
    });
  };

  const showPeriodForm = (period = null) => {
    setSelectedPeriod(period);
    periodForm.resetFields();
    if (period) {
      periodForm.setFieldsValue({
        ...period,
        startDate: dateValue(period.startDate),
        endDate: dateValue(period.endDate),
        paymentDate: dateValue(period.paymentDate),
        selectedProfileIds: period.selectedProfileIds || [],
      });
    } else {
      const month = dayjs();
      periodForm.setFieldsValue({
        name: month.format("MMMM YYYY"),
        startDate: month.startOf("month"),
        endDate: month.endOf("month"),
        paymentDate: month.endOf("month"),
        prorationMethod: "Calendar Days",
        prorateNewHires: true,
        prorateExits: true,
        status: "Open",
        payrollType: "Regular",
        includeRegularSalary: true,
        selectedProfileIds: [],
        paymentAccountId:
          paymentAccounts.find(
            (account) => account.isDefault && account.isActive,
          )?.paymentAccountId || null,
      });
    }
    setPeriodOpen(true);
  };

  const submitPeriod = async () => {
    const values = await periodForm.validateFields();
    const result = await savePeriod(selectedPeriod?.periodId, {
      ...values,
      startDate: isoDate(values.startDate),
      endDate: isoDate(values.endDate),
      paymentDate: isoDate(values.paymentDate),
    });
    if (result) setPeriodOpen(false);
  };

  const runDraft = async (period) => {
    const run = await generateDraft(period.periodId);
    if (run) setSelectedRun(run);
  };

  const regenerateSelectedDraft = async () => {
    if (!selectedRun) return;
    const run = await generateDraft(selectedRun.periodId);
    if (run) setSelectedRun(run);
  };

  const applyWorkflow = async (actionName, data = {}) => {
    const result = await transitionRun(selectedRun.runId, actionName, data);
    if (result) setSelectedRun(result);
    return result;
  };

  const confirmWorkflow = (
    actionName,
    title,
    content,
    okText,
    danger = false,
  ) => {
    Modal.confirm({
      title,
      content,
      okText,
      okButtonProps: { danger },
      onOk: () => applyWorkflow(actionName),
    });
  };

  const returnRunToDraft = () => {
    let comment = "";
    const wasApproved = selectedRun?.status === "Approved";
    const requiresReason = selectedRun?.status !== "Rejected";
    Modal.confirm({
      title: wasApproved
        ? "Reopen this approved payroll?"
        : "Return payroll to Draft?",
      content: (
        <>
          <Alert
            type="warning"
            showIcon
            message={
              wasApproved
                ? "The approval is retained in audit history, but the payroll must be regenerated before it can be submitted again."
                : "The payroll will need to be regenerated before it can be submitted again."
            }
            style={{ marginBottom: 12 }}
          />
          <Input.TextArea
            rows={3}
            placeholder={
              requiresReason ? "Reason (required)" : "Reason (optional)"
            }
            onChange={(event) => {
              comment = event.target.value;
            }}
          />
        </>
      ),
      okText: "Return to Draft",
      onOk: async () => {
        if (requiresReason && !comment.trim())
          throw new Error("Enter a reason");
        return applyWorkflow("returnToDraft", { comment });
      },
    });
  };

  const submitRejection = async () => {
    const values = await rejectionForm.validateFields();
    const result = await applyWorkflow("reject", values);
    if (result) {
      setRejectOpen(false);
      rejectionForm.resetFields();
    }
  };

  const showExport = () => {
    setExportPreview(null);
    setExportState("bank");
  };

  const previewExport = async () => {
    const result = await previewPaymentExport(selectedRun);
    if (result) setExportPreview(result);
  };

  const downloadExport = async () => {
    await downloadPaymentExport(selectedRun);
  };

  const showPaymentForm = (payment) => {
    setSelectedPayment(payment);
    paymentForm.resetFields();
    paymentForm.setFieldsValue({ paymentDate: dayjs() });
  };

  const submitPayment = async () => {
    const values = await paymentForm.validateFields();
    const result = await recordPayment(selectedPayment.paymentId, {
      ...values,
      paymentDate: isoDate(values.paymentDate),
    });
    if (result) {
      setSelectedRun(result);
      setSelectedPayment(null);
    }
  };

  const showBulkPaymentForm = () => {
    bulkPaymentForm.resetFields();
    bulkPaymentForm.setFieldsValue({ paymentDate: dayjs() });
    setBulkPaymentOpen(true);
  };

  const submitBulkPayment = async () => {
    const values = await bulkPaymentForm.validateFields();
    const result = await markPayrollPaid(selectedRun.runId, {
      ...values,
      paymentDate: isoDate(values.paymentDate),
    });
    if (result) {
      setSelectedRun(result);
      setBulkPaymentOpen(false);
    }
  };

  const submitReversal = async () => {
    const values = await reversalForm.validateFields();
    const result = await reversePayroll(selectedRun.runId, values);
    if (result) {
      setSelectedRun(result.reversedRun);
      setReversalOpen(false);
      reversalForm.resetFields();
    }
  };

  const openInputManager = async (period) => {
    setInputPeriod(period);
    await Promise.all([fetchInputs(period.periodId), fetchInputOptions()]);
  };

  const showInputForm = (payrollInput = null) => {
    setSelectedInput(payrollInput);
    inputForm.resetFields();
    if (payrollInput) {
      inputForm.setFieldsValue({
        profileId: payrollInput.profileId,
        componentId: payrollInput.componentId,
        amount: Number(payrollInput.amount),
        comment: payrollInput.comment,
      });
    }
    setInputOpen(true);
  };

  const submitInput = async () => {
    const values = await inputForm.validateFields();
    const result = await saveInput(selectedInput?.inputId, {
      ...values,
      periodId: inputPeriod.periodId,
      amount: String(values.amount),
    });
    if (result) setInputOpen(false);
  };

  const inputComponents = useMemo(
    () =>
      components.filter(
        (component) =>
          component.isActive &&
          ["One-Off", "Manual Input"].includes(component.frequency) &&
          component.calculationMethod !== "Employee Basic Salary" &&
          (!inputPeriod ||
            !component.effectiveFrom ||
            !dayjs(component.effectiveFrom).isAfter(
              inputPeriod.endDate,
              "day",
            )) &&
          (!inputPeriod ||
            !component.effectiveTo ||
            !dayjs(component.effectiveTo).isBefore(
              inputPeriod.startDate,
              "day",
            )),
      ),
    [components, inputPeriod],
  );

  const inputEmployees = useMemo(() => {
    if (!inputPeriod) return [];
    return profiles.filter(
      (profile) =>
        profile.payrollEligible &&
        profile.employmentStatus !== "Suspended" &&
        (!profile.hireDate ||
          !dayjs(profile.hireDate).isAfter(inputPeriod.endDate, "day")) &&
        (!profile.exitDate ||
          !dayjs(profile.exitDate).isBefore(inputPeriod.startDate, "day")) &&
        (inputPeriod.payrollType === "Regular" ||
          inputPeriod.selectedProfileIds?.includes(profile.profileId)),
    );
  }, [inputPeriod, profiles]);
  const inputsEditable = Boolean(
    inputPeriod?.status === "Open" &&
    (!inputPeriod?.run || inputPeriod.run.status === "Draft"),
  );

  const componentColumns = [
    { title: "Component", dataIndex: "componentName" },
    { title: "Type", dataIndex: "kind", render: (value) => <Tag>{value}</Tag> },
    { title: "Tax treatment", dataIndex: "taxTreatment" },
    { title: "Amount", dataIndex: "amount", align: "right", render: money },
    {
      title: "Source",
      dataIndex: "isAutomatic",
      render: (value) => (
        <Tag color={value ? "blue" : "orange"}>
          {value ? "Automatic" : "Needs input"}
        </Tag>
      ),
    },
    { title: "Note", dataIndex: "note", render: (value) => value || "—" },
  ];

  const employeeColumns = [
    {
      title: "Employee",
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Text strong>{row.employeeName}</Text>
          <Text type="secondary">
            {row.staffCode || "No staff ID"} ·{" "}
            {row.departmentName || "No department"}
          </Text>
        </Space>
      ),
    },
    {
      title: "Basic",
      dataIndex: "basicSalary",
      align: "right",
      render: (value, row) => (
        <Space direction="vertical" size={0}>
          <Text>{money(value)}</Text>
          {(row.salarySegments?.length > 1 ||
            Number(row.prorationFactor) < 1) && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Monthly rate {money(row.contractBasicSalary)}
            </Text>
          )}
        </Space>
      ),
    },
    { title: "Gross", dataIndex: "grossPay", align: "right", render: money },
    { title: "PAYE", dataIndex: "paye", align: "right", render: money },
    {
      title: "Employee NSSF",
      dataIndex: "employeeNssf",
      align: "right",
      render: money,
    },
    { title: "LST", dataIndex: "lst", align: "right", render: money },
    {
      title: "Other deductions",
      dataIndex: "otherDeductions",
      align: "right",
      render: money,
    },
    {
      title: "Net pay",
      dataIndex: "netPay",
      align: "right",
      render: (value) => <Text strong>{money(value)}</Text>,
    },
    {
      title: "Warnings",
      dataIndex: "warnings",
      render: (warnings) => {
        const messages = warningMessages(warnings);
        if (!messages.length) return <Tag color="success">None</Tag>;
        return (
          <Popover
            title="Calculation warnings"
            content={<WarningList warnings={messages} />}
            trigger="click"
          >
            <Button
              type="link"
              size="small"
              aria-label={`View ${messages.length} calculation warning${messages.length === 1 ? "" : "s"}`}
              onClick={(event) => event.stopPropagation()}
              style={{ height: "auto", padding: 0 }}
            >
              <Tag
                color="warning"
                style={{ cursor: "pointer", marginInlineEnd: 0 }}
              >
                {messages.length}
              </Tag>
            </Button>
          </Popover>
        );
      },
    },
    {
      title: "Review",
      render: (_, row) =>
        canWorkflow("finance_payroll_review_comment") ? (
          <Button
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              addReviewComment({
                targetType: "Employee",
                profileId: row.profileId,
              });
            }}
          >
            Comment
          </Button>
        ) : null,
    },
  ];

  const payslipColumns = [
    {
      title: "Employee",
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Text strong>{row.employeeName}</Text>
          <Text type="secondary">
            {row.staffCode || "No staff ID"} ·{" "}
            {row.employeeEmail || "No email address"}
          </Text>
        </Space>
      ),
    },
    { title: "Payslip number", dataIndex: "payslipNumber" },
    {
      title: "Verification",
      dataIndex: "verificationCode",
      render: (value) => value || "—",
    },
    {
      title: "Gross pay",
      dataIndex: "grossPay",
      align: "right",
      render: money,
    },
    {
      title: "Deductions",
      dataIndex: "totalDeductions",
      align: "right",
      render: money,
    },
    {
      title: "Net pay",
      dataIndex: "netPay",
      align: "right",
      render: (value) => <Text strong>{money(value)}</Text>,
    },
    {
      title: "Last email",
      render: (_, row) => {
        const delivery = row.deliveries?.[0];
        return delivery ? (
          <Tag
            color={
              delivery.status === "Sent"
                ? "green"
                : delivery.status === "Failed"
                  ? "red"
                  : "blue"
            }
          >
            {delivery.status}
          </Tag>
        ) : (
          <Text type="secondary">Not sent</Text>
        );
      },
    },
    {
      title: "Actions",
      width: 145,
      align: "center",
      render: (_, row) => (
        <Space size={0}>
          <Tooltip title="Download PDF">
            <Button
              type="text"
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => downloadPayslip(row)}
            />
          </Tooltip>
          <Tooltip title="Open PDF to print">
            <Button
              type="text"
              size="small"
              icon={<PrinterOutlined />}
              onClick={() => downloadPayslip(row, true)}
            />
          </Tooltip>
          <Tooltip
            title={
              row.employeeEmail
                ? `Email to ${row.employeeEmail}`
                : "This employee has no email address"
            }
          >
            <Button
              type="text"
              size="small"
              icon={<MailOutlined />}
              disabled={
                !row.employeeEmail ||
                saving ||
                !canWorkflow("finance_payroll_payslips_email")
              }
              onClick={async () => {
                const run = await emailPayslip(
                  selectedRun.runId,
                  row.payslipId,
                );
                if (run) setSelectedRun(run);
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const paymentColumns = [
    {
      title: "Employee",
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Text strong>{row.employeeName}</Text>
          <Text type="secondary">{row.staffCode || "No staff ID"}</Text>
        </Space>
      ),
    },
    {
      title: "Bank account",
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Text>
            {row.bankName}
            {row.bankCode ? ` (${row.bankCode})` : ""}
          </Text>
          <Text type="secondary">{row.maskedBankAccount}</Text>
        </Space>
      ),
    },
    { title: "Net salary", dataIndex: "amount", align: "right", render: money },
    {
      title: "Status",
      dataIndex: "status",
      filters: [
        { text: "Unpaid", value: "Unpaid" },
        { text: "Paid", value: "Paid" },
      ],
      onFilter: (value, row) => row.status === value,
      render: (value) => (
        <Tag color={value === "Paid" ? "green" : "orange"}>{value}</Tag>
      ),
    },
    {
      title: "Payment details",
      render: (_, row) =>
        row.status === "Paid" ? (
          <Space direction="vertical" size={0}>
            <Text>{row.paymentReference}</Text>
            <Text type="secondary">
              {dayjs(row.paymentDate).format("DD MMM YYYY")}
            </Text>
          </Space>
        ) : (
          <Text type="secondary">Not recorded</Text>
        ),
    },
    {
      title: "Action",
      width: 90,
      align: "center",
      render: (_, row) =>
        row.status === "Unpaid" &&
        canWorkflow("finance_payroll_payments_record") ? (
          <Tooltip title="Record bank payment reference">
            <Button
              type="text"
              size="small"
              icon={<DollarCircleOutlined />}
              onClick={() => showPaymentForm(row)}
            />
          </Tooltip>
        ) : null,
    },
  ];

  const inputColumns = [
    {
      title: "Employee",
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Text strong>{row.employeeName}</Text>
          <Text type="secondary">{row.staffCode || "No staff ID"}</Text>
        </Space>
      ),
    },
    {
      title: "Input",
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Text>{row.componentName}</Text>
          <Tag
            color={
              row.componentKind === "Earning"
                ? "green"
                : row.componentKind === "Employee Deduction"
                  ? "red"
                  : "blue"
            }
          >
            {row.componentKind}
          </Tag>
        </Space>
      ),
    },
    { title: "Amount", dataIndex: "amount", align: "right", render: money },
    { title: "Reason / comment", dataIndex: "comment" },
    {
      title: "Actions",
      width: 100,
      align: "center",
      render: (_, row) => (
        <Space size="small">
          <Tooltip title="Edit input">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => showInputForm(row)}
              disabled={!inputsEditable}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this monthly input?"
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => deleteInput(row.inputId, inputPeriod.periodId)}
          >
            <Tooltip title="Delete input">
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                disabled={saving || !inputsEditable}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const columns = [
    {
      title: "Payroll period",
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Text strong>{row.name}</Text>
          <Tag
            color={
              row.payrollType === "Regular"
                ? "default"
                : row.payrollType === "Correction"
                  ? "red"
                  : "purple"
            }
          >
            {row.payrollType || "Regular"}
          </Tag>
          <Text type="secondary">
            Pay on {dayjs(row.paymentDate).format("DD MMM YYYY")}
          </Text>
          <Text type="secondary">
            From{" "}
            {row.paymentAccountName || "No school payment account selected"}
          </Text>
          <Text type="secondary">{row.prorationMethod} proration</Text>
        </Space>
      ),
    },
    {
      title: "Period dates",
      render: (_, row) =>
        `${dayjs(row.startDate).format("DD MMM")} — ${dayjs(row.endDate).format("DD MMM YYYY")}`,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (value) => <Tag color="blue">{value}</Tag>,
    },
    {
      title: "Monthly inputs",
      dataIndex: "inputCount",
      align: "center",
      render: (value) => <Tag>{value || 0}</Tag>,
    },
    {
      title: "Payroll run",
      render: (_, row) =>
        row.run ? (
          <Space direction="vertical" size={0}>
            <Tag color={workflowColors[row.run.status]}>{row.run.status}</Tag>
            {row.run.needsRegeneration && (
              <Tag color="orange">Needs regeneration</Tag>
            )}
            <Text>
              {row.run.employeeCount} employees · {money(row.run.totalNetPay)}{" "}
              net
            </Text>
            <Text type="secondary">
              Generated {dayjs(row.run.generatedAt).format("DD MMM YYYY HH:mm")}
            </Text>
          </Space>
        ) : (
          <Tag>Not generated</Tag>
        ),
    },
    {
      title: "Actions",
      width: 210,
      align: "center",
      render: (_, row) => (
        <Space size="small">
          <Tooltip title="Monthly inputs and adjustments">
            <Button
              type="text"
              size="small"
              icon={<FormOutlined />}
              onClick={() => openInputManager(row)}
            />
          </Tooltip>
          {(!row.run || row.run.status === "Draft") && (
            <Tooltip title={row.run ? "Regenerate draft" : "Generate draft"}>
              <Button
                type="text"
                size="small"
                icon={row.run ? <ReloadOutlined /> : <CalculatorOutlined />}
                loading={saving}
                onClick={() => runDraft(row)}
              />
            </Tooltip>
          )}
          {row.run && (
            <Tooltip title="View draft">
              <Button
                type="text"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => setSelectedRun(row.run)}
              />
            </Tooltip>
          )}
          {!row.run && (
            <Tooltip title="Edit period">
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => showPeriodForm(row)}
              />
            </Tooltip>
          )}
          {(!row.run || row.run.status === "Draft") && (
            <Popconfirm
              title={
                row.run
                  ? "Delete this draft payroll?"
                  : "Delete this payroll period?"
              }
              description={
                row.run
                  ? "The payroll period will remain available."
                  : undefined
              }
              okText="Delete"
              okButtonProps={{ danger: true }}
              onConfirm={() =>
                row.run ? deleteRun(row.run.runId) : deletePeriod(row.periodId)
              }
            >
              <Tooltip title={row.run ? "Delete draft" : "Delete period"}>
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  disabled={saving}
                />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <div>
        <Title level={3} style={{ marginBottom: 4 }}>
          Payroll runs
        </Title>
        <Text type="secondary">
          Create monthly periods and review calculated draft payroll before
          approval.
        </Text>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={12} lg={6}>
          <Card>
            <Statistic title="Payroll periods" value={periods.length} />
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card>
            <Statistic title="Runs generated" value={totals.runs} />
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card>
            <Statistic title="Employees in drafts" value={totals.employees} />
          </Card>
        </Col>
        <Col xs={24} lg={6}>
          <Card>
            <Statistic
              title="Total draft net pay"
              value={money(totals.netPay)}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <div style={{ textAlign: "right", marginBottom: 16 }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => showPeriodForm()}
          >
            Create payroll period
          </Button>
        </div>
        <Table
          rowKey="periodId"
          columns={columns}
          dataSource={periods}
          loading={loading}
          scroll={{ x: 900 }}
        />
      </Card>

      <Modal
        title={selectedPeriod ? "Edit payroll period" : "Create payroll period"}
        open={periodOpen}
        onCancel={() => setPeriodOpen(false)}
        onOk={submitPeriod}
        confirmLoading={saving}
        okText="Save period"
      >
        <Form form={periodForm} layout="vertical">
          <Form.Item
            name="name"
            label="Period name"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="startDate"
                label="Start date"
                rules={[{ required: true }]}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="endDate"
                label="End date"
                dependencies={["startDate"]}
                rules={[{ required: true }]}
              >
                <DatePicker
                  disabledDate={(current) =>
                    Boolean(
                      periodStart && current?.isBefore(periodStart, "day"),
                    )
                  }
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="paymentDate"
                label="Payment date"
                rules={[{ required: true }]}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="paymentAccountId"
                label="School payment account"
                rules={
                  paymentAccounts.length
                    ? [
                        {
                          required: true,
                          message:
                            "Select the account that will fund this payroll.",
                        },
                      ]
                    : []
                }
              >
                <Select
                  allowClear
                  placeholder={
                    paymentAccounts.length
                      ? "Select payment account"
                      : "Configure one under Payroll Setup"
                  }
                  options={paymentAccounts
                    .filter((account) => account.isActive)
                    .map((account) => ({
                      value: account.paymentAccountId,
                      label: `${account.name} — ${account.bankName} ${account.maskedAccountNumber}`,
                    }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item
            name="payrollType"
            label="Payroll type"
            rules={[{ required: true }]}
          >
            <Select
              disabled={Boolean(selectedPeriod)}
              onChange={(value) => {
                periodForm.setFieldValue(
                  "includeRegularSalary",
                  value !== "Off-Cycle",
                );
              }}
              options={[
                { value: "Regular", label: "Regular — normal monthly payroll" },
                {
                  value: "Off-Cycle",
                  label: "Off-cycle — missed or special payment",
                },
                ...(selectedPeriod?.payrollType === "Correction"
                  ? [
                      {
                        value: "Correction",
                        label:
                          "Correction — replacement for a reversed payroll",
                      },
                    ]
                  : []),
              ]}
            />
          </Form.Item>
          {payrollType !== "Regular" && (
            <>
              <Form.Item
                name="selectedProfileIds"
                label="Employees included"
                rules={[
                  {
                    required: true,
                    type: "array",
                    min: 1,
                    message: "Select at least one employee",
                  },
                ]}
              >
                <Select
                  mode="multiple"
                  showSearch
                  optionFilterProp="label"
                  options={profiles.map((profile) => ({
                    value: profile.profileId,
                    label: `${profile.staffName || profile.staffEmail} — ${profile.staffCode || "No staff ID"}`,
                  }))}
                />
              </Form.Item>
              <Form.Item
                name="includeRegularSalary"
                label="Include normal salary and recurring components"
                valuePropName="checked"
                tooltip="Keep this off for a missed bonus or allowance. Turn it on only when the special run must pay the employee's normal salary too."
              >
                <Switch disabled={payrollType === "Correction"} />
              </Form.Item>
              {payrollType === "Off-Cycle" && (
                <Alert
                  type="info"
                  showIcon
                  message="Add the missed earning or deduction under Monthly Inputs before generating this off-cycle payroll."
                  style={{ marginBottom: 16 }}
                />
              )}
            </>
          )}
          <Form.Item
            name="prorationMethod"
            label="Joiner/leaver salary calculation"
            rules={[{ required: true }]}
            tooltip="Controls how much of the monthly salary is paid when an employee works only part of this period."
          >
            <Select
              options={[
                {
                  value: "Calendar Days",
                  label: "Calendar days — count every day in the period",
                },
                {
                  value: "Working Days",
                  label: "Working days — count Monday to Friday",
                },
                {
                  value: "No Proration",
                  label: "No proration — always pay the full month",
                },
              ]}
            />
          </Form.Item>
          <Space size="large" wrap>
            <Form.Item
              name="prorateNewHires"
              label="Prorate new hires"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
            <Form.Item
              name="prorateExits"
              label="Prorate employees who leave"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Space>
        </Form>
      </Modal>

      <Drawer
        title={
          inputPeriod
            ? `Monthly inputs — ${inputPeriod.name}`
            : "Monthly inputs"
        }
        open={Boolean(inputPeriod)}
        onClose={() => setInputPeriod(null)}
        width="84%"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => showInputForm()}
            disabled={!inputsEditable}
          >
            Add input
          </Button>
        }
      >
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Alert
            type="info"
            showIcon
            message="Use this for amounts that change only in this payroll period."
            description="Bonus, overtime and arrears use earning components. Salary advances and other authorised payroll deductions use employee-deduction components. SACCO loan data is read-only and is not entered here. Every entry needs a reason."
          />
          {inputPeriod?.run && (
            <Alert
              type={inputPeriod.run.status === "Draft" ? "warning" : "error"}
              showIcon
              message={
                inputPeriod.run.status === "Draft"
                  ? "This period already has a draft. Regenerate it after changing inputs."
                  : `Inputs are locked because this payroll is ${inputPeriod.run.status}.`
              }
            />
          )}
          {!inputComponents.length && (
            <Alert
              type="warning"
              showIcon
              message="No monthly-input components are available."
              description="In Payroll Setup, create an active One-Off or Manual Input component first."
            />
          )}
          <Table
            rowKey="inputId"
            columns={inputColumns}
            dataSource={inputs}
            loading={loading}
            scroll={{ x: 800 }}
          />
        </Space>
      </Drawer>

      <Modal
        title={
          selectedInput
            ? "Edit monthly payroll input"
            : "Add monthly payroll input"
        }
        open={inputOpen}
        onCancel={() => setInputOpen(false)}
        onOk={submitInput}
        confirmLoading={saving}
        okText="Save input"
      >
        <Form form={inputForm} layout="vertical">
          <Form.Item
            name="profileId"
            label="Employee"
            rules={[{ required: true, message: "Select an employee" }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              options={inputEmployees.map((profile) => ({
                value: profile.profileId,
                label: `${profile.staffName || profile.staffEmail} — ${profile.staffCode || "No staff ID"}${profile.isReady ? "" : " (setup incomplete)"}`,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="componentId"
            label="Earning or deduction"
            rules={[{ required: true, message: "Select a component" }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              options={inputComponents.map((component) => ({
                value: component.componentId,
                label: `${component.name} — ${component.kind}`,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="amount"
            label="Amount (UGX)"
            rules={[{ required: true, message: "Enter the amount" }]}
          >
            <InputNumber
              {...payrollAmountInputProps}
              min={0.01}
              precision={2}
              style={{ width: "100%" }}
            />
          </Form.Item>
          <Form.Item
            name="comment"
            label="Reason / comment"
            rules={[
              {
                required: true,
                whitespace: true,
                message: "Explain why this amount is being added",
              },
            ]}
          >
            <Input.TextArea rows={3} maxLength={255} showCount />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title={selectedRun ? `Payroll — ${selectedRun.periodName}` : "Payroll"}
        open={Boolean(selectedRun)}
        onClose={() => setSelectedRun(null)}
        width="92%"
      >
        {selectedRun && (
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            <Card size="small">
              <Row gutter={[16, 12]} align="middle" justify="space-between">
                <Col>
                  <Space direction="vertical" size={2}>
                    <Space>
                      <Tag color={workflowColors[selectedRun.status]}>
                        {selectedRun.status}
                      </Tag>
                      {selectedRun.isLocked && (
                        <Text type="secondary">
                          <LockOutlined /> Payroll figures are locked
                        </Text>
                      )}
                    </Space>
                    <Text type="secondary">
                      Paying account:{" "}
                      {selectedRun.paymentAccountSnapshot?.name
                        ? `${selectedRun.paymentAccountSnapshot.name} · ${selectedRun.paymentAccountSnapshot.maskedAccountNumber}`
                        : "Not configured"}
                    </Text>
                  </Space>
                </Col>
                <Col>
                  <Space wrap>
                    {selectedRun.status === "Draft" &&
                      selectedRun.needsRegeneration && (
                        <Button
                          icon={<ReloadOutlined />}
                          loading={saving}
                          onClick={regenerateSelectedDraft}
                        >
                          Regenerate draft
                        </Button>
                      )}
                    {["Draft", "Submitted", "In Review", "Approved"].includes(
                      selectedRun.status,
                    ) &&
                      canWorkflow("finance_payroll_payslips_view") &&
                      canWorkflow("finance_payroll_sensitive_salary_full") && (
                        <Button
                          icon={<EyeOutlined />}
                          onClick={() => downloadDraftPayslips(selectedRun)}
                        >
                          Draft payslip preview
                        </Button>
                      )}
                    {selectedRun.status === "Draft" &&
                      canWorkflow("finance_payroll_workflow_submit") && (
                        <Button
                          type="primary"
                          icon={<SendOutlined />}
                          loading={saving}
                          disabled={selectedRun.needsRegeneration}
                          onClick={() =>
                            confirmWorkflow(
                              "submit",
                              "Submit this payroll?",
                              "Employees, inputs and calculated figures will be locked for review.",
                              "Submit",
                            )
                          }
                        >
                          Submit
                        </Button>
                      )}
                    {selectedRun.status === "Submitted" &&
                      canWorkflow("finance_payroll_workflow_review") && (
                        <Button
                          type="primary"
                          icon={<AuditOutlined />}
                          loading={saving}
                          onClick={() =>
                            confirmWorkflow(
                              "review",
                              "Start payroll review?",
                              "This records who began the formal review.",
                              "Start review",
                            )
                          }
                        >
                          Start review
                        </Button>
                      )}
                    {selectedRun.status === "In Review" &&
                      canCompleteApprovalStep && (
                        <Button
                          type="primary"
                          icon={<CheckCircleOutlined />}
                          loading={saving}
                          onClick={() =>
                            confirmWorkflow(
                              "approve",
                              completingReviewStep
                                ? "Complete payroll review?"
                                : "Give final payroll approval?",
                              completingReviewStep
                                ? "This records that the payroll checks and figures have been reviewed. A different authorized user must provide final approval."
                                : "Final approval locks the payroll period and figures. An authorized finance user can then finalize it and create payslips.",
                              completingReviewStep
                                ? "Complete review"
                                : "Give final approval",
                            )
                          }
                        >
                          {completingReviewStep
                            ? "Complete review"
                            : "Give final approval"}
                        </Button>
                      )}
                    {selectedRun.status === "In Review" &&
                      canWorkflow("finance_payroll_workflow_reject") && (
                        <Button
                          danger
                          icon={<CloseCircleOutlined />}
                          loading={saving}
                          onClick={() => setRejectOpen(true)}
                        >
                          Reject
                        </Button>
                      )}
                    {["Submitted", "In Review", "Rejected"].includes(
                      selectedRun.status,
                    ) &&
                      canWorkflow(
                        "finance_payroll_workflow_return_to_draft",
                      ) && (
                        <Button
                          icon={<RollbackOutlined />}
                          loading={saving}
                          onClick={returnRunToDraft}
                        >
                          {selectedRun.status === "Rejected"
                            ? "Return to Draft"
                            : "Reopen for correction"}
                        </Button>
                      )}
                    {selectedRun.status === "Approved" &&
                      canWorkflow(
                        "finance_payroll_workflow_return_to_draft",
                      ) && (
                        <Button
                          icon={<RollbackOutlined />}
                          loading={saving}
                          onClick={returnRunToDraft}
                        >
                          Reopen for correction
                        </Button>
                      )}
                    {selectedRun.status === "Approved" &&
                      canWorkflow("finance_payroll_finalize") && (
                        <Button
                          type="primary"
                          icon={<LockOutlined />}
                          loading={saving}
                          onClick={() =>
                            confirmWorkflow(
                              "finalize",
                              "Finalize this payroll?",
                              "This creates permanent calculation snapshots and employee payslips. Figures can only be corrected through a documented reversal before payment.",
                              "Finalize",
                            )
                          }
                        >
                          Finalize & create payslips
                        </Button>
                      )}
                    {selectedRun.status === "Finalized" &&
                      canWorkflow("finance_payroll_reverse") && (
                        <Button
                          danger
                          icon={<RollbackOutlined />}
                          loading={saving}
                          onClick={() => setReversalOpen(true)}
                        >
                          Reverse & correct
                        </Button>
                      )}
                    {[
                      "Draft",
                      "Submitted",
                      "In Review",
                      "Rejected",
                      "Approved",
                    ].includes(selectedRun.status) &&
                      canWorkflow("finance_payroll_workflow_cancel") && (
                        <Button
                          danger
                          icon={<CloseCircleOutlined />}
                          onClick={cancelPayroll}
                        >
                          Cancel
                        </Button>
                      )}
                  </Space>
                </Col>
              </Row>
            </Card>

            {selectedRun.status === "Draft" &&
              selectedRun.needsRegeneration && (
                <Alert
                  type="warning"
                  showIcon
                  message="This Draft payroll is out of date"
                  description={
                    selectedRun.regenerationReason ||
                    "Salary, employee, bank, or payroll input data changed after this calculation. Regenerate the draft before submitting it."
                  }
                  action={
                    <Button size="small" onClick={regenerateSelectedDraft}>
                      Regenerate now
                    </Button>
                  }
                />
              )}

            {selectedRun.status === "Approved" && (
              <Alert
                type="success"
                showIcon
                message={`Approved by ${selectedRun.approvedByName || "an authorized user"}`}
                description={
                  selectedRun.approvedAt
                    ? dayjs(selectedRun.approvedAt).format("DD MMM YYYY HH:mm")
                    : null
                }
              />
            )}
            {selectedRun.status === "Finalized" && (
              <Alert
                type="success"
                showIcon
                message={`Finalized with ${selectedRun.payslipCount || 0} permanent payslip(s)`}
                description={`Finalized by ${selectedRun.finalizedByName || "an authorized user"}${selectedRun.finalizedAt ? ` on ${dayjs(selectedRun.finalizedAt).format("DD MMM YYYY HH:mm")}` : ""}. These figures can no longer change.`}
              />
            )}
            {selectedRun.status === "Partially Paid" && (
              <Alert
                type="warning"
                showIcon
                message="Some employees are paid and some are still unpaid."
                description="Each confirmed employee payment is recorded separately."
              />
            )}
            {selectedRun.status === "Paid" && (
              <Alert
                type="success"
                showIcon
                message={`Payroll fully paid${selectedRun.paymentBatchReference ? ` — ${selectedRun.paymentBatchReference}` : ""}`}
                description={`Recorded by ${selectedRun.paidByName || "an authorized user"}${selectedRun.paidAt ? ` on ${dayjs(selectedRun.paidAt).format("DD MMM YYYY HH:mm")}` : ""}.`}
              />
            )}
            {selectedRun.status === "Reversed" && (
              <Alert
                type="error"
                showIcon
                message="This payroll was reversed and is excluded from statutory and management totals."
                description={`${selectedRun.reversalReason || "No reason recorded"}${selectedRun.reversedByName ? ` — ${selectedRun.reversedByName}` : ""}`}
              />
            )}
            {selectedRun.rejectionReason &&
              selectedRun.status !== "Approved" && (
                <Alert
                  type="error"
                  showIcon
                  message="Payroll rejection reason"
                  description={selectedRun.rejectionReason}
                />
              )}
            <Row gutter={[16, 16]}>
              <Col xs={12} lg={4}>
                <Card>
                  <Statistic
                    title="Employees"
                    value={selectedRun.employeeCount}
                  />
                </Card>
              </Col>
              <Col xs={12} lg={4}>
                <Card>
                  <Statistic
                    title="Gross pay"
                    value={money(selectedRun.totalGrossPay)}
                  />
                </Card>
              </Col>
              <Col xs={12} lg={4}>
                <Card>
                  <Statistic
                    title="PAYE"
                    value={money(selectedRun.totalPaye)}
                  />
                </Card>
              </Col>
              <Col xs={12} lg={4}>
                <Card>
                  <Statistic
                    title="Employee NSSF"
                    value={money(selectedRun.totalEmployeeNssf)}
                  />
                </Card>
              </Col>
              <Col xs={12} lg={4}>
                <Card>
                  <Statistic title="LST" value={money(selectedRun.totalLst)} />
                </Card>
              </Col>
              <Col xs={12} lg={4}>
                <Card>
                  <Statistic
                    title="Net pay"
                    value={money(selectedRun.totalNetPay)}
                  />
                </Card>
              </Col>
            </Row>

            {canWorkflow("finance_payroll_review_view") && (
              <Card
                title="Review checks and approval path"
                extra={
                  canWorkflow("finance_payroll_review_comment") && (
                    <Button size="small" onClick={addReviewComment}>
                      Add blocking comment
                    </Button>
                  )
                }
              >
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Alert
                    showIcon
                    type={
                      reviewDashboard?.summary?.readyToSubmit
                        ? "success"
                        : "warning"
                    }
                    message={
                      reviewDashboard?.summary?.readyToSubmit
                        ? "All automatic checks are ready"
                        : `${reviewDashboard?.summary?.openBlocking || 0} blocking issue(s), ${reviewDashboard?.summary?.openWarnings || 0} warning(s)`
                    }
                    description={
                      reviewDashboard?.previousPayroll
                        ? `Compared with ${reviewDashboard.previousPayroll.periodName}. Calculation version ${reviewDashboard.calculationVersion}.`
                        : "No earlier regular payroll is available for comparison."
                    }
                  />
                  <Table
                    size="small"
                    rowKey="resultId"
                    pagination={{ pageSize: 6 }}
                    dataSource={reviewDashboard?.results || []}
                    columns={[
                      {
                        title: "Level",
                        dataIndex: "severity",
                        render: (v) => (
                          <Tag
                            color={
                              v === "Blocking"
                                ? "red"
                                : v === "Warning"
                                  ? "gold"
                                  : "blue"
                            }
                          >
                            {v}
                          </Tag>
                        ),
                      },
                      {
                        title: "Employee / area",
                        render: (_, r) => r.employeeName || r.category,
                      },
                      { title: "Check", dataIndex: "title" },
                      { title: "Details", dataIndex: "detail" },
                      { title: "Status", dataIndex: "status" },
                      {
                        title: "",
                        render: (_, r) =>
                          r.severity === "Warning" &&
                          r.status === "Open" &&
                          canWorkflow("finance_payroll_review_acknowledge") ? (
                            <Button
                              size="small"
                              onClick={() =>
                                reasonAction(
                                  "Acknowledge this warning",
                                  `${domainSettings.urls.finance.payrollValidationResults}/${r.resultId}/acknowledge`,
                                )
                              }
                            >
                              Acknowledge
                            </Button>
                          ) : r.severity === "Blocking" &&
                            r.canOverride &&
                            r.status === "Open" &&
                            canWorkflow("finance_payroll_review_resolve") ? (
                            <Button
                              size="small"
                              onClick={() =>
                                reasonAction(
                                  "Resolve this controlled exception",
                                  `${domainSettings.urls.finance.payrollValidationResults}/${r.resultId}/resolve`,
                                )
                              }
                            >
                              Resolve
                            </Button>
                          ) : null,
                      },
                    ]}
                  />
                  <Timeline
                    items={(selectedRun.approvalSteps || []).map((step) => ({
                      color:
                        step.status === "Approved"
                          ? "green"
                          : step.status === "Rejected"
                            ? "red"
                            : "blue",
                      children: (
                        <div>
                          <Text strong>
                            {step.sequence}. {step.name}
                          </Text>{" "}
                          <Tag>{step.stepType}</Tag>
                          <div>
                            <Text type="secondary">
                              {step.status}
                              {step.decidedByName
                                ? ` by ${step.decidedByName}`
                                : ""}
                              {step.delegatedFromName
                                ? ` (delegated by ${step.delegatedFromName})`
                                : ""}
                            </Text>
                          </div>
                        </div>
                      ),
                    }))}
                  />
                  {reviewComments.map((thread) => (
                    <Alert
                      key={thread.commentId}
                      type={
                        thread.isBlocking && thread.status !== "Resolved"
                          ? "error"
                          : "info"
                      }
                      message={`${thread.targetType} comment · ${thread.status}`}
                      description={
                        <Space direction="vertical">
                          <Text>{thread.comment}</Text>
                          <Text type="secondary">
                            {thread.authorName} ·{" "}
                            {dayjs(thread.createdAt).format(
                              "DD MMM YYYY HH:mm",
                            )}
                          </Text>
                          {(thread.replies || []).map((reply) => (
                            <Text key={reply.commentId}>
                              ↳ {reply.authorName}: {reply.comment}
                            </Text>
                          ))}
                          <Space>
                            {canWorkflow("finance_payroll_review_comment") && (
                              <Button
                                size="small"
                                onClick={() =>
                                  addReviewComment({
                                    targetType: thread.targetType,
                                    profileId: thread.profileId,
                                    componentCode: thread.componentCode,
                                    parentId: thread.commentId,
                                  })
                                }
                              >
                                Reply
                              </Button>
                            )}
                            {thread.isBlocking &&
                              thread.status !== "Resolved" &&
                              canWorkflow("finance_payroll_review_resolve") && (
                                <Button
                                  size="small"
                                  onClick={() =>
                                    reasonAction(
                                      "Resolve this review comment",
                                      `${domainSettings.urls.finance.payrollReviewComments}/${thread.commentId}/resolve`,
                                    )
                                  }
                                >
                                  Resolve with reason
                                </Button>
                              )}
                            {thread.status === "Resolved" &&
                              canWorkflow("finance_payroll_review_resolve") && (
                                <Button
                                  size="small"
                                  onClick={() =>
                                    reasonAction(
                                      "Reopen this review comment",
                                      `${domainSettings.urls.finance.payrollReviewComments}/${thread.commentId}/reopen`,
                                    )
                                  }
                                >
                                  Reopen
                                </Button>
                              )}
                          </Space>
                        </Space>
                      }
                    />
                  ))}
                </Space>
              </Card>
            )}

            {selectedRun.excludedEmployeeCount > 0 && (
              <Alert
                type="warning"
                showIcon
                message={`${selectedRun.excludedEmployeeCount} employee(s) were excluded because their setup is incomplete.`}
                description={selectedRun.generationWarnings?.map((warning) => (
                  <div key={warning.profileId}>
                    {warning.employee}: {warning.issues.join(", ")}
                  </div>
                ))}
              />
            )}

            <Table
              rowKey="runEmployeeId"
              columns={employeeColumns}
              dataSource={selectedRun.employees}
              scroll={{ x: 1250 }}
              onRow={(employee) => {
                const expanded = expandedEmployeeRowKeys.includes(
                  employee.runEmployeeId,
                );
                return {
                  tabIndex: 0,
                  "aria-expanded": expanded,
                  "aria-label": `${employee.employeeName} payroll details, ${expanded ? "expanded" : "collapsed"}. Press Enter or Space to ${expanded ? "collapse" : "expand"}.`,
                  style: { cursor: "pointer" },
                  onKeyDown: (event) => {
                    if (
                      event.target !== event.currentTarget ||
                      !["Enter", " "].includes(event.key)
                    ) {
                      return;
                    }
                    event.preventDefault();
                    setExpandedEmployeeRowKeys((currentKeys) =>
                      currentKeys.includes(employee.runEmployeeId)
                        ? currentKeys.filter(
                            (key) => key !== employee.runEmployeeId,
                          )
                        : [...currentKeys, employee.runEmployeeId],
                    );
                  },
                };
              }}
              expandable={{
                expandRowByClick: true,
                expandedRowKeys: expandedEmployeeRowKeys,
                onExpandedRowsChange: setExpandedEmployeeRowKeys,
                expandedRowRender: (employee) => (
                  <Space
                    direction="vertical"
                    size="small"
                    style={{ width: "100%" }}
                  >
                    {employee.prorationNote && (
                      <Alert
                        type="info"
                        showIcon
                        message={employee.prorationNote}
                      />
                    )}
                    {employee.salarySegments?.length > 1 && (
                      <Table
                        rowKey="segmentId"
                        size="small"
                        pagination={false}
                        dataSource={employee.salarySegments}
                        columns={[
                          {
                            title: "Salary period",
                            render: (_, segment) =>
                              `${dayjs(segment.appliedFrom).format("DD MMM YYYY")} – ${dayjs(segment.appliedTo).format("DD MMM YYYY")}`,
                          },
                          {
                            title: "Monthly rate",
                            dataIndex: "salaryRate",
                            align: "right",
                            render: money,
                          },
                          {
                            title: "Units",
                            render: (_, segment) =>
                              `${segment.units}/${segment.periodUnits}`,
                          },
                          {
                            title: "Prorated amount",
                            dataIndex: "amount",
                            align: "right",
                            render: money,
                          },
                        ]}
                      />
                    )}
                    {warningMessages(employee.warnings).length > 0 && (
                      <Alert
                        type="warning"
                        showIcon
                        message={`${warningMessages(employee.warnings).length} calculation warning${warningMessages(employee.warnings).length === 1 ? "" : "s"}`}
                        description={
                          <WarningList warnings={employee.warnings} />
                        }
                      />
                    )}
                    <SaccoLoanInformation
                      snapshot={employee.saccoLoanSnapshot}
                    />
                    <Table
                      rowKey="itemId"
                      columns={[
                        ...componentColumns,
                        {
                          title: "Review",
                          render: (_, item) =>
                            canWorkflow("finance_payroll_review_comment") ? (
                              <Button
                                size="small"
                                onClick={() =>
                                  addReviewComment({
                                    targetType: "Line",
                                    profileId: employee.profileId,
                                    componentCode: item.componentCode,
                                    componentName: item.componentName,
                                  })
                                }
                              >
                                Comment
                              </Button>
                            ) : null,
                        },
                      ]}
                      dataSource={employee.items}
                      pagination={false}
                      size="small"
                    />
                  </Space>
                ),
              }}
            />

            {["Finalized", "Partially Paid", "Paid"].includes(
              selectedRun.status,
            ) &&
              canWorkflow("finance_payroll_payments_view") && (
                <Card
                  title="Payment processing"
                  size="small"
                  extra={
                    <Space wrap>
                      {canWorkflow("finance_payroll_bank_export") &&
                        canWorkflow("finance_payroll_sensitive_bank") &&
                        canWorkflow(
                          "finance_payroll_sensitive_salary_full",
                        ) && (
                          <Tooltip
                            title={
                              selectedRun.status === "Paid"
                                ? "Download the full bank payment schedule"
                                : "Download a bank schedule containing unpaid employees"
                            }
                          >
                            <Button
                              size="small"
                              icon={<BankOutlined />}
                              onClick={showExport}
                            >
                              Bank schedule
                            </Button>
                          </Tooltip>
                        )}
                      {["Finalized", "Partially Paid"].includes(
                        selectedRun.status,
                      ) &&
                        selectedRun.paymentSummary?.unpaidEmployees > 0 &&
                        canWorkflow("finance_payroll_mark_paid") && (
                          <Button
                            type="primary"
                            size="small"
                            icon={<DollarCircleOutlined />}
                            onClick={showBulkPaymentForm}
                          >
                            Mark all remaining Paid
                          </Button>
                        )}
                    </Space>
                  }
                >
                  <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                    <Col xs={12} md={6}>
                      <Statistic
                        title="Employees"
                        value={selectedRun.paymentSummary?.totalEmployees || 0}
                      />
                    </Col>
                    <Col xs={12} md={6}>
                      <Statistic
                        title="Paid"
                        value={selectedRun.paymentSummary?.paidEmployees || 0}
                        valueStyle={{ color: "#228B22" }}
                      />
                    </Col>
                    <Col xs={12} md={6}>
                      <Statistic
                        title="Unpaid"
                        value={selectedRun.paymentSummary?.unpaidEmployees || 0}
                        valueStyle={{ color: "#d46b08" }}
                      />
                    </Col>
                    <Col xs={12} md={6}>
                      <Statistic
                        title="Unpaid amount"
                        value={money(selectedRun.paymentSummary?.unpaidAmount)}
                      />
                    </Col>
                  </Row>
                  <Table
                    rowKey="paymentId"
                    columns={paymentColumns}
                    dataSource={selectedRun.payments || []}
                    pagination={false}
                    scroll={{ x: 950 }}
                  />
                </Card>
              )}

            {["Finalized", "Partially Paid", "Paid"].includes(
              selectedRun.status,
            ) &&
              canWorkflow("finance_payroll_payslips_view") &&
              canWorkflow("finance_payroll_sensitive_salary_full") && (
                <Card
                  title="Employee payslips"
                  size="small"
                  extra={
                    <Space wrap>
                      <Tooltip title="Refresh payslip and email statuses">
                        <Button
                          size="small"
                          icon={<ReloadOutlined />}
                          onClick={async () => {
                            const run = await fetchRun(selectedRun.runId);
                            if (run) setSelectedRun(run);
                          }}
                        />
                      </Tooltip>
                      <Tooltip title="Download one PDF containing every employee payslip">
                        <Button
                          size="small"
                          icon={<DownloadOutlined />}
                          onClick={() => downloadCombinedPayslips(selectedRun)}
                        >
                          Combined PDF
                        </Button>
                      </Tooltip>
                      <Tooltip title="Download separate employee PDFs in one ZIP file">
                        <Button
                          size="small"
                          icon={<DownloadOutlined />}
                          onClick={() => downloadPayslipsZip(selectedRun)}
                        >
                          ZIP
                        </Button>
                      </Tooltip>
                      {canWorkflow("finance_payroll_final_schedule_export") && (
                        <>
                          <Button
                            size="small"
                            icon={<FileExcelOutlined />}
                            onClick={() =>
                              downloadFinalSchedule(selectedRun, "xlsx")
                            }
                          >
                            Final schedule
                          </Button>
                          <Button
                            size="small"
                            onClick={() =>
                              downloadFinalSchedule(selectedRun, "csv")
                            }
                          >
                            CSV
                          </Button>
                          <Button
                            size="small"
                            onClick={() =>
                              downloadFinalSchedule(selectedRun, "pdf")
                            }
                          >
                            PDF
                          </Button>
                        </>
                      )}
                      <Tooltip title="Open the combined PDF to print">
                        <Button
                          size="small"
                          icon={<PrinterOutlined />}
                          onClick={() =>
                            downloadCombinedPayslips(selectedRun, true)
                          }
                        >
                          Print all
                        </Button>
                      </Tooltip>
                      {canWorkflow("finance_payroll_payslips_email") && (
                        <Popconfirm
                          title="Email all payslips?"
                          description="Each payslip will be sent separately to that employee's email address."
                          onConfirm={async () => {
                            const run = await emailAllPayslips(
                              selectedRun.runId,
                            );
                            if (run) setSelectedRun(run);
                          }}
                        >
                          <Button
                            size="small"
                            icon={<MailOutlined />}
                            loading={saving}
                          >
                            Email all
                          </Button>
                        </Popconfirm>
                      )}
                      {canWorkflow("finance_payroll_payslips_email") &&
                        emailSummary?.failed > 0 && (
                          <Button
                            size="small"
                            danger
                            icon={<ReloadOutlined />}
                            loading={saving}
                            onClick={async () => {
                              const run = await emailAllPayslips(
                                selectedRun.runId,
                                true,
                              );
                              if (run) setSelectedRun(run);
                              fetchEmailSummary(selectedRun.runId);
                            }}
                          >
                            Retry failed ({emailSummary.failed})
                          </Button>
                        )}
                    </Space>
                  }
                >
                  {emailSummary && (
                    <Alert
                      showIcon
                      type={emailSummary.failed ? "warning" : "info"}
                      style={{ marginBottom: 12 }}
                      message={`Email progress: ${emailSummary.sent} sent, ${emailSummary.pending} pending, ${emailSummary.failed} failed, ${emailSummary.missingEmail} missing addresses`}
                    />
                  )}
                  <Table
                    rowKey="payslipId"
                    columns={payslipColumns}
                    dataSource={selectedRun.payslips || []}
                    pagination={false}
                    scroll={{ x: 1050 }}
                  />
                </Card>
              )}

            <Card title="Audit history" size="small">
              <Timeline
                items={(selectedRun.history || []).map((entry) => ({
                  color: workflowColors[entry.toStatus] || "blue",
                  children: (
                    <Space direction="vertical" size={0}>
                      <Text strong>{entry.action}</Text>
                      <Text type="secondary">
                        {entry.actorName || "System"} ·{" "}
                        {dayjs(entry.createdAt).format("DD MMM YYYY HH:mm")}
                      </Text>
                      {entry.comment && <Text>{entry.comment}</Text>}
                    </Space>
                  ),
                }))}
              />
            </Card>
          </Space>
        )}
      </Drawer>

      <Modal
        title="Preview and download bank payment file"
        open={Boolean(exportState)}
        onCancel={() => setExportState(null)}
        footer={
          <Space>
            <Button onClick={previewExport}>Preview and validate</Button>
            <Button
              type="primary"
              disabled={
                !exportPreview ||
                Boolean(exportPreview.issues?.length) ||
                exportPreview.balanced === false
              }
              icon={<DownloadOutlined />}
              onClick={downloadExport}
            >
              Download file
            </Button>
          </Space>
        }
        width={900}
      >
        <Alert
          type="info"
          showIcon
          message="System bank payment format"
          description="The Excel layout is controlled by the payroll system and cannot be edited by users."
          style={{ marginBottom: 16 }}
        />
        {exportPreview && (
          <>
            <Alert
              showIcon
              type={exportPreview.issues?.length ? "warning" : "success"}
              message={`${exportPreview.rowCount ?? exportPreview.employeeCount} row(s) · ${formatPayrollMoney(exportPreview.totalAmount ?? exportPreview.exportTotal)}`}
              description={
                exportPreview.issues?.length
                  ? `${exportPreview.issues.length} employee(s) have missing required fields. Correct them before downloading.`
                  : "The selected records and total are valid for the bank payment file."
              }
            />
            <Table
              style={{ marginTop: 16 }}
              size="small"
              pagination={{ pageSize: 5 }}
              dataSource={(exportPreview.rows || []).map((item, index) => ({
                key: index,
                values: Array.isArray(item) ? item : item.values,
              }))}
              columns={(exportPreview.headings || []).map((heading, index) => ({
                title: heading,
                render: (_, item) => item.values[index],
              }))}
              scroll={{ x: true }}
            />
          </>
        )}
      </Modal>

      <Modal
        title="Reject payroll"
        open={rejectOpen}
        onCancel={() => setRejectOpen(false)}
        onOk={submitRejection}
        okText="Reject payroll"
        okButtonProps={{ danger: true }}
        confirmLoading={saving}
      >
        <Alert
          type="warning"
          showIcon
          message="The payroll will remain locked until it is explicitly returned to Draft."
          style={{ marginBottom: 16 }}
        />
        <Form form={rejectionForm} layout="vertical">
          <Form.Item
            name="rejectionReason"
            label="Rejection reason"
            rules={[
              {
                required: true,
                whitespace: true,
                message: "Explain why this payroll is rejected",
              },
            ]}
          >
            <Input.TextArea rows={4} maxLength={1000} showCount />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={
          selectedPayment
            ? `Record payment — ${selectedPayment.employeeName}`
            : "Record employee payment"
        }
        open={Boolean(selectedPayment)}
        onCancel={() => setSelectedPayment(null)}
        onOk={submitPayment}
        okText="Mark employee Paid"
        confirmLoading={saving}
      >
        {selectedPayment && (
          <Alert
            type="info"
            showIcon
            message={`${money(selectedPayment.amount)} to ${selectedPayment.bankName} ${selectedPayment.maskedBankAccount}`}
            style={{ marginBottom: 16 }}
          />
        )}
        <Form form={paymentForm} layout="vertical">
          <Form.Item
            name="paymentReference"
            label="Bank payment reference"
            rules={[
              {
                required: true,
                whitespace: true,
                message: "Enter the bank payment reference",
              },
            ]}
          >
            <Input maxLength={120} />
          </Form.Item>
          <Form.Item
            name="paymentDate"
            label="Payment date"
            rules={[{ required: true }]}
          >
            <DatePicker
              disabledDate={(current) => current?.isAfter(dayjs(), "day")}
              style={{ width: "100%" }}
            />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={3} maxLength={1000} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Mark all remaining employees Paid"
        open={bulkPaymentOpen}
        onCancel={() => setBulkPaymentOpen(false)}
        onOk={submitBulkPayment}
        okText="Mark all Paid"
        confirmLoading={saving}
      >
        <Alert
          type="warning"
          showIcon
          message={`${selectedRun?.paymentSummary?.unpaidEmployees || 0} unpaid employee(s), totalling ${money(selectedRun?.paymentSummary?.unpaidAmount)}`}
          description="Use this only after the bank confirms the salary batch. The payment records cannot be edited afterward."
          style={{ marginBottom: 16 }}
        />
        <Form form={bulkPaymentForm} layout="vertical">
          <Form.Item
            name="batchReference"
            label="Bank batch reference"
            rules={[
              {
                required: true,
                whitespace: true,
                message: "Enter the bank batch reference",
              },
            ]}
          >
            <Input maxLength={120} />
          </Form.Item>
          <Form.Item
            name="paymentDate"
            label="Payment date"
            rules={[{ required: true }]}
          >
            <DatePicker
              disabledDate={(current) => current?.isAfter(dayjs(), "day")}
              style={{ width: "100%" }}
            />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={3} maxLength={1000} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Reverse payroll and create correction"
        open={reversalOpen}
        onCancel={() => setReversalOpen(false)}
        onOk={submitReversal}
        okText="Reverse & create correction"
        okButtonProps={{ danger: true }}
        confirmLoading={saving}
      >
        <Alert
          type="warning"
          showIcon
          message="The original payroll will remain permanently visible but will be excluded from report totals."
          description="A linked correction period containing the same employees will be created. Review its employee details and inputs, generate it, and send it through approval normally."
          style={{ marginBottom: 16 }}
        />
        <Form form={reversalForm} layout="vertical">
          <Form.Item
            name="reason"
            label="Reason for reversal"
            rules={[
              {
                required: true,
                whitespace: true,
                message: "Explain why this payroll is being reversed",
              },
            ]}
          >
            <Input.TextArea rows={4} maxLength={2000} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

export default PayrollRunsManagement;
